import React from 'react';
import {
  ClipboardDocumentListIcon,
  ClockIcon,
  UserIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function BackToWorkDashboardHelp() {
  return (
    <div className="bg-gradient-to-br from-teal-50 to-cyan-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-teal-600 p-3 rounded-full">
              <ClipboardDocumentListIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Back-to-Work Dashboard</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive guide to managing employee return-to-work processes,
            tracking outstanding forms, and ensuring smooth reintegration after absences.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ChartBarIcon className="h-6 w-6 text-teal-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The Back-to-Work Dashboard provides HR managers with a centralized view of all
            return-to-work forms, outstanding submissions, and reintegration progress.
            Track completion rates, schedule meetings, and generate comprehensive reports
            to ensure employees return to work safely and effectively.
          </p>
        </div>

        {/* Key Metrics */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-teal-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Dashboard Metrics</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-teal-600 mr-3" />
                <h3 className="text-xl font-semibold text-teal-800">Outstanding Forms</h3>
              </div>
              <p className="text-teal-700 mb-4">Track forms that require immediate attention and follow-up</p>
              <ul className="space-y-2 text-teal-600">
                <li>• Forms submitted but not yet reviewed</li>
                <li>• Incomplete submissions requiring clarification</li>
                <li>• Forms pending manager approval</li>
                <li>• Overdue submissions needing escalation</li>
              </ul>
            </div>

            <div className="bg-cyan-50 border-l-4 border-cyan-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-cyan-600 mr-3" />
                <h3 className="text-xl font-semibold text-cyan-800">Total Forms</h3>
              </div>
              <p className="text-cyan-700 mb-4">Complete overview of all return-to-work submissions</p>
              <ul className="space-y-2 text-cyan-600">
                <li>• Historical submission data</li>
                <li>• Completion rate tracking</li>
                <li>• Pattern analysis for absence management</li>
                <li>• Reporting and compliance metrics</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Form Management */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-teal-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Form Management</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Real-time Updates:</strong> Dashboard refreshes automatically when forms are submitted</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Form Navigation:</strong> Click on any form to view detailed information and take action</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Status Tracking:</strong> Visual indicators show form completion and review status</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Bulk Operations:</strong> Process multiple forms efficiently with batch actions</span>
              </li>
            </ul>
          </div>

          {/* Meeting Management */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <CalendarDaysIcon className="h-6 w-6 text-teal-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Meeting Management</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Return-to-Work Meetings:</strong> Schedule and document reintegration discussions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Fit Assessment:</strong> Evaluate employee's readiness to return to work</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Support Planning:</strong> Document required workplace adjustments and accommodations</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Meeting Notes:</strong> Record discussions, decisions, and action items</span>
              </li>
            </ul>
          </div>

          {/* Reporting Features */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ChartBarIcon className="h-6 w-6 text-teal-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Reporting & Analytics</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Sick Leave Reports:</strong> Generate comprehensive absence analysis reports</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Trend Analysis:</strong> Identify patterns in absences and return-to-work success</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Compliance Tracking:</strong> Monitor adherence to return-to-work policies</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Performance Metrics:</strong> Track dashboard usage and form completion rates</span>
              </li>
            </ul>
          </div>

          {/* AI Integration */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="h-6 w-6 text-teal-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">AI-Powered Insights</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Risk Assessment:</strong> AI analysis of return-to-work risks and recommendations</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Pattern Recognition:</strong> Identify trends in absence reasons and recovery times</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Automated Alerts:</strong> Notifications for high-risk cases or overdue forms</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Smart Recommendations:</strong> AI-suggested adjustments and support measures</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Process Flow */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-8 mb-8 text-white">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">Return-to-Work Process Flow</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <h3 className="font-semibold mb-2">Form Submission</h3>
              <p className="text-teal-100 text-sm">Employee submits return-to-work form with health status</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <h3 className="font-semibold mb-2">Manager Review</h3>
              <p className="text-teal-100 text-sm">HR reviews form and assesses employee's readiness</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <h3 className="font-semibold mb-2">Meeting & Assessment</h3>
              <p className="text-teal-100 text-sm">Conduct return-to-work interview and fit assessment</p>
            </div>

            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">4</span>
              </div>
              <h3 className="font-semibold mb-2">Reintegration</h3>
              <p className="text-teal-100 text-sm">Implement support measures and monitor progress</p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Best Practices for Return-to-Work Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📋 Proactive Monitoring</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Regularly check dashboard for new submissions</li>
                <li>• Set up alerts for overdue forms</li>
                <li>• Review outstanding forms daily</li>
                <li>• Track completion rates and identify bottlenecks</li>
                <li>• Monitor employee progress post-return</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🤝 Supportive Approach</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Schedule meetings promptly after form submission</li>
                <li>• Listen actively to employee concerns</li>
                <li>• Assess genuine fitness for work</li>
                <li>• Implement reasonable adjustments</li>
                <li>• Provide clear communication and expectations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use swipe gestures to navigate between forms</li>
                <li>• Tap form cards to expand detailed views</li>
                <li>• Access dashboard on mobile for remote management</li>
                <li>• Review forms during meetings using tablet devices</li>
                <li>• Save important notes and decisions immediately</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Data-Driven Decisions</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Generate regular absence reports</li>
                <li>• Analyze return-to-work success rates</li>
                <li>• Identify common absence reasons</li>
                <li>• Track effectiveness of support measures</li>
                <li>• Use insights to improve policies</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔒 Confidentiality</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Handle medical information with care</li>
                <li>• Share information only on need-to-know basis</li>
                <li>• Comply with data protection regulations</li>
                <li>• Maintain secure records of all forms</li>
                <li>• Respect employee privacy rights</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⚡ Efficiency Tips</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use templates for common meeting notes</li>
                <li>• Batch process similar forms together</li>
                <li>• Set up recurring review schedules</li>
                <li>• Automate routine follow-up reminders</li>
                <li>• Leverage AI insights for faster decisions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}